<script lang="ts">
  import { onMount } from "svelte";
  import { competitorServicesApi } from "@shared/api/competitor-services";
  import { productOptionsStore } from "@entities/product/store.svelte";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import { canEditMasters } from "@shared/types/session";
  import { navigate } from "@shared/router/navigation";
  import { formatDate } from "@shared/util/format";
  import CompetitorProductsEditor from "@features/competitor-products-edit/CompetitorProductsEditor.svelte";
  import ReferralSettingEditor from "@features/referral-setting-edit/ReferralSettingEditor.svelte";
  import ExternalSolutionSalesMaterialsEditor from "@widgets/external-solution-sales-materials/ExternalSolutionSalesMaterialsEditor.svelte";
  import InlineField from "@shared/ui/InlineField.svelte";
  import {
    THIRD_PARTY_SOLUTION_NAME_MAX_LENGTH,
    externalSolutionKindLabel,
    isReferralKind,
    normalizeThirdPartySolutionErrorMessage,
    validateThirdPartySolutionName,
  } from "@domain/competitor-service/third-party-solution-edit";
  import type {
    CompetitorServiceDetail,
    CompetitorServiceUsageTarget,
  } from "@shared/types/competitor-service";

  interface Props {
    id: string;
  }

  type DetailTab = "overview" | "competitors" | "referral" | "usage";

  let { id }: Props = $props();

  let detail = $state<CompetitorServiceDetail | null>(null);
  let loading = $state(false);
  let errorMessage = $state<string | null>(null);
  let activeTab = $state<DetailTab>("overview");

  const allowEditMasters = $derived(
    sessionStore.user ? canEditMasters(sessionStore.user.role) : false,
  );
  const isReferral = $derived(
    Boolean(detail && isReferralKind(detail.service)),
  );
  const blockedProductNames = $derived(
    productOptionsStore.data.map((product) => product.name),
  );

  function normalizeError(err: unknown, fallback: string): string {
    return err instanceof Error
      ? normalizeThirdPartySolutionErrorMessage(err.message)
      : fallback;
  }

  async function load(): Promise<void> {
    loading = true;
    errorMessage = null;
    try {
      detail = await competitorServicesApi.get(id);
    } catch (err) {
      errorMessage = normalizeError(
        err,
        "外部ソリューションを読み込めませんでした",
      );
      detail = null;
    } finally {
      loading = false;
    }
  }

  async function saveName(next: string): Promise<void> {
    if (!detail || !allowEditMasters) return;
    const trimmed = next.trim();
    const validationError = validateThirdPartySolutionName(
      trimmed,
      blockedProductNames,
    );
    if (validationError) throw new Error(validationError);
    if (trimmed === detail.service.name) return;

    try {
      const saved = await competitorServicesApi.patch(detail.service.id, {
        name: trimmed,
      });
      detail = {
        ...detail,
        service: {
          ...detail.service,
          ...saved,
        },
      };
    } catch (err) {
      throw new Error(normalizeError(err, "名称の保存に失敗しました"));
    }
  }

  function settingUsageLabel(): string {
    return detail ? externalSolutionKindLabel(detail.service.kind) : "その他";
  }

  function detailFieldText(value: string | null | undefined): string {
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : "-";
  }

  function targetHref(row: CompetitorServiceUsageTarget): string {
    if (row.target_kind === "firm" && row.firm_id)
      return `/firms/${row.firm_id}`;
    if (row.target_kind === "lawyer" && row.lawyer_id)
      return `/lawyers/${row.lawyer_id}`;
    return "/firms";
  }

  function targetKindLabel(row: CompetitorServiceUsageTarget): string {
    return row.target_kind === "firm" ? "法律事務所" : "弁護士";
  }

  function navigateTo(ev: MouseEvent, href: string): void {
    ev.preventDefault();
    navigate(href);
  }

  onMount(() => {
    void load();
    if (allowEditMasters) void productOptionsStore.load();
  });
</script>

<p class="back-link">
  <a href="/third-party-solutions">← 外部ソリューション一覧へ</a>
</p>

{#if loading && detail === null}
  <p class="status">読み込み中…</p>
{:else if errorMessage && detail === null}
  <p class="status error">{errorMessage}</p>
{:else if detail}
  <header class="detail-header">
    <h1>{detail.service.name}</h1>
  </header>

  {#if errorMessage}
    <p class="status error">{errorMessage}</p>
  {/if}

  <div
    class="tabs"
    data-testid="external-solution-detail-tabs"
    aria-label="外部ソリューション詳細タブ"
    role="tablist"
  >
    <button
      type="button"
      id="external-solution-tab-overview"
      role="tab"
      class:active={activeTab === "overview"}
      aria-selected={activeTab === "overview"}
      aria-controls="external-solution-section-overview"
      data-testid="external-solution-tab-overview"
      onclick={() => (activeTab = "overview")}
    >
      概要
    </button>
    <button
      type="button"
      id="external-solution-tab-competitors"
      role="tab"
      class:active={activeTab === "competitors"}
      aria-selected={activeTab === "competitors"}
      aria-controls="external-solution-section-competitors"
      data-testid="external-solution-tab-competitors"
      onclick={() => (activeTab = "competitors")}
    >
      競合ソリューション
    </button>
    <button
      type="button"
      id="external-solution-tab-referral"
      role="tab"
      class:active={activeTab === "referral"}
      aria-selected={activeTab === "referral"}
      aria-controls="external-solution-section-referral"
      data-testid="external-solution-tab-referral"
      onclick={() => (activeTab = "referral")}
    >
      紹介情報
    </button>
    <button
      type="button"
      id="external-solution-tab-usage"
      role="tab"
      class:active={activeTab === "usage"}
      aria-selected={activeTab === "usage"}
      aria-controls="external-solution-section-usage"
      data-testid="external-solution-tab-usage"
      onclick={() => (activeTab = "usage")}
    >
      利用顧客
    </button>
  </div>

  {#if activeTab === "overview"}
    <div
      id="external-solution-section-overview"
      class="panel"
      data-testid="external-solution-section-overview"
      role="tabpanel"
      aria-labelledby="external-solution-tab-overview"
    >
      <h2>概要</h2>
      <div class="info-grid">
        <div
          class="name-field"
          data-testid="external-solution-name-inline"
        >
          <InlineField
            label="外部ソリューション名"
            value={detail.service.name}
            type="text"
            maxlength={THIRD_PARTY_SOLUTION_NAME_MAX_LENGTH}
            disabled={!allowEditMasters}
            onSave={saveName}
          />
        </div>
        <div class="read-field">
          <span class="field-label">区分</span>
          <span class="field-value"
            >{externalSolutionKindLabel(detail.service.kind)}</span
          >
        </div>
        <div class="read-field">
          <span class="field-label">提供会社</span>
          <span class="field-value"
            >{detailFieldText(detail.service.provider_company_name)}</span
          >
        </div>
        <div class="read-field">
          <span class="field-label">サービスサイトURL</span>
          <span class="field-value"
            >{detailFieldText(detail.service.service_site_url)}</span
          >
        </div>
        <div class="read-field">
          <span class="field-label">説明</span>
          <span class="field-value multiline"
            >{detailFieldText(detail.service.summary)}</span
          >
        </div>
        <div class="read-field">
          <span class="field-label">価格</span>
          <span class="field-value multiline"
            >{detailFieldText(detail.service.price_detail)}</span
          >
        </div>
        <div class="materials-field">
          <span class="field-label">営業資料</span>
          <ExternalSolutionSalesMaterialsEditor
            competitorServiceId={detail.service.id}
            canEdit={allowEditMasters}
          />
        </div>
        <div class="read-field">
          <span class="field-label">メモ</span>
          <span class="field-value multiline"
            >{detailFieldText(detail.service.memo)}</span
          >
        </div>
      </div>
    </div>
  {:else if activeTab === "competitors"}
    <div
      id="external-solution-section-competitors"
      class="panel"
      data-testid="external-solution-section-competitors"
      role="tabpanel"
      aria-labelledby="external-solution-tab-competitors"
    >
      <CompetitorProductsEditor rows={detail.competitor_products} />
    </div>
  {:else if activeTab === "referral"}
    <div
      id="external-solution-section-referral"
      class="panel"
      data-testid="external-solution-section-referral"
      role="tabpanel"
      aria-labelledby="external-solution-tab-referral"
    >
      {#if !isReferral}
        <p class="kind-notice" data-testid="referral-kind-notice">
          紹介先区分の外部ソリューションのみ紹介情報を表示します。
        </p>
      {/if}
      <ReferralSettingEditor
        competitorServiceId={detail.service.id}
        setting={detail.referral_setting}
        canEdit={allowEditMasters && isReferral}
        onChanged={load}
      />
    </div>
  {:else if activeTab === "usage"}
    <div
      id="external-solution-section-usage"
      class="panel"
      data-testid="external-solution-section-usage"
      role="tabpanel"
      aria-labelledby="external-solution-tab-usage"
    >
      <h2>利用顧客</h2>
      {#if detail.usage_targets.length === 0}
        <p class="muted">利用登録先はありません。</p>
      {:else}
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>顧客名</th>
                <th>区分</th>
                <th>利用登録先</th>
                <th>更新拒絶期限</th>
                <th>契約終了日</th>
              </tr>
            </thead>
            <tbody>
              {#each detail.usage_targets as row (row.id)}
                <tr>
                  <td>
                    <a
                      href={targetHref(row)}
                      onclick={(ev) => navigateTo(ev, targetHref(row))}
                    >
                      {row.target_name}
                    </a>
                  </td>
                  <td>{settingUsageLabel()}</td>
                  <td>{targetKindLabel(row)}</td>
                  <td>{formatDate(row.renewal_rejection_deadline)}</td>
                  <td>{formatDate(row.contract_end_date)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {/if}
{/if}

<style>
  .back-link {
    margin: 0 0 8px;
  }
  .back-link a {
    color: #2563eb;
    text-decoration: none;
    font-size: 0.875rem;
  }
  .back-link a:hover {
    text-decoration: underline;
  }
  .detail-header {
    margin-bottom: 16px;
  }
  .detail-header h1 {
    margin: 0;
    font-size: 1.4rem;
  }
  .tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 16px;
    gap: 4px;
    flex-wrap: wrap;
  }
  .tabs button {
    border: 0;
    border-radius: 0;
    padding: 8px 12px;
    background: transparent;
    color: #6b7280;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    font: inherit;
    white-space: nowrap;
  }
  .tabs button.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
    font-weight: 700;
  }
  .panel {
    margin-top: 12px;
  }
  .panel h2 {
    margin-top: 0;
    font-size: 1rem;
  }
  .info-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px 16px;
    margin: 0;
  }
  .read-field {
    display: grid;
    grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
    align-items: baseline;
    gap: 4px 16px;
    padding: 8px 0;
    border-bottom: 1px solid #eef2f7;
  }
  .name-field {
    padding: 8px 0;
    border-bottom: 1px solid #eef2f7;
  }
  .name-field :global(.inline-field) {
    display: grid;
    grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
    align-items: baseline;
    gap: 4px 16px;
  }
  .name-field :global(.field-label) {
    color: #64748b;
    font-weight: 700;
  }
  .name-field :global(.inline-field-control) {
    color: #1f2937;
    font-size: 13px;
    line-height: 1.6;
  }
  .field-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 700;
  }
  .field-value {
    min-width: 0;
    color: #1f2937;
    font-size: 13px;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
  .field-value.multiline {
    white-space: pre-wrap;
  }
  .materials-field {
    display: grid;
    grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
    align-items: start;
    gap: 4px 16px;
    padding: 8px 0;
    border-bottom: 1px solid #eef2f7;
  }
  .materials-field .field-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 700;
    padding-top: 8px;
  }
  .kind-notice {
    margin: 0 0 12px;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    background: #eff6ff;
    color: #1e40af;
    padding: 9px 10px;
    font-size: 13px;
    font-weight: 700;
  }
  .table-scroll {
    overflow: auto;
    border-top: 1px solid var(--border, #e3e6eb);
    -webkit-overflow-scrolling: touch;
  }
  thead th {
    background: #f9fafb;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .status {
    color: #6b7280;
  }
  .status.error {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 10px 12px;
  }
  @media (max-width: 720px) {
    .read-field {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    .name-field :global(.inline-field) {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    .materials-field {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    .materials-field .field-label {
      padding-top: 0;
    }
  }
</style>
